export enum DoceboCourseType {
  LEARNING_PLAN = 'learning_plan',
  ELEARNING = 'elearning',
  WEBINAR = 'webinar',
  CLASSROOM = 'classroom',
}

export interface DoceboCatalogueView {
  extra_data: {
    name: string;
    description: string;
    code: string;
    slug: string;
  };
  data: {
    items: DoceboCatalogueViewItem[];
  };
  catalogueId?: number;
}

interface BaseDoceboCatalogueViewItem {
  item_id: string;
  item_name: string;
  item_description: string;
  item_code: string;
  item_slug: string;
}

export interface DoceboCatalogueViewLearningPlan
  extends BaseDoceboCatalogueViewItem {
  course_type: DoceboCourseType.LEARNING_PLAN;
  number_of_courses: string;
}

export interface DoceboCatalogueViewELearning
  extends BaseDoceboCatalogueViewItem {
  course_type: DoceboCourseType.ELEARNING;
}

export interface DoceboCatalogueViewWebinar
  extends BaseDoceboCatalogueViewItem {
  course_type: DoceboCourseType.WEBINAR;
}

export interface DoceboCatalogueViewClassroom
  extends BaseDoceboCatalogueViewItem {
  course_type: DoceboCourseType.CLASSROOM;
}

export type DoceboCatalogueViewItem =
  | DoceboCatalogueViewLearningPlan
  | DoceboCatalogueViewELearning
  | DoceboCatalogueViewWebinar
  | DoceboCatalogueViewClassroom;

export interface DoceboUserSessionInfo {
  data: {
    id: number;
  };
}

export interface DoceboUserInfo {
  data: {
    additional_fields: {
      id: number;
      title: string;
      value: string | number;
    }[];
    branches: {
      id: number;
    }[];
  };
}

interface DoceboEnrolments {
  data: {
    count: number;
    current_page: number;
    current_page_size: number;
    has_more_data: boolean;
    total_count: number;
    total_page_count: number;
    items: (DoceboEnrolmentItemCourse | DoceboEnrolmentItemLearningPlan)[];
  };
}

export type DoceboEnrolmentStatus =
  | 'locked'
  | 'waiting'
  | 'enrolled'
  | 'in-progress'
  | 'completed';

export type DoceboLoEnrolmentStatus =
  | 'not_started'
  | 'ab-initio'
  | 'in_progress'
  | 'failed'
  | 'completed';

interface BaseDoceboEnrolmentItem {
  id: number;
  status: DoceboEnrolmentStatus;
}

export interface DoceboEnrolmentItemElearning extends BaseDoceboEnrolmentItem {
  type: DoceboCourseType.ELEARNING;
}

export interface DoceboEnrolmentItemWebinar extends BaseDoceboEnrolmentItem {
  type: DoceboCourseType.WEBINAR;
}

export interface DoceboEnrolmentItemClassroom extends BaseDoceboEnrolmentItem {
  type: DoceboCourseType.CLASSROOM;
}

export interface DoceboEnrolmentItemLearningPlan
  extends BaseDoceboEnrolmentItem {
  type: DoceboCourseType.LEARNING_PLAN;
  courses_count: number;
  complete_percent: number;
}

export type DoceboEnrolmentItemCourse =
  | DoceboEnrolmentItemElearning
  | DoceboEnrolmentItemWebinar
  | DoceboEnrolmentItemClassroom;

interface DoceboOAuth2Response {
  access_token: string;
  expires_in: number;
  scope: string;
  token_type: string;
}

export type DoceboCourseStatus =
  | 'locked'
  | 'non_subscribed'
  | 'in_progress'
  | 'completed';

export interface DoceboCourseDetail {
  data: {
    id: number;
    tree: {
      id: string;
      status: DoceboLoEnrolmentStatus;
      locked?: 'locked';
      type: 'scormorg' | 'tincan' | 'file' | 'test' | string;
    }[];
  };
}

export interface DoceboLearningPlanDetail {
  data: {
    id: number;
    courses: DoceboLearningPlanDetailCourse[];
  };
}

export interface DoceboLearningPlanDetailCourse {
  idCourse: number;
  name: string;
  description: string;
  course_type: DoceboCourseType;
  status: DoceboCourseStatus;
}

export interface DoceboLearningObjectPlayDetail {
  data: {
    launch_params: {
      url?: string;
      mp4_url?: string;
      bookmark?: number;
    };
  };
}

export interface DoceboLearningObjectTrackDetail {
  data: {
    acknowledge: {
      url: string;
    };
  };
}

export interface DoceboEnrolUserInCourseResponse {
  data: {
    errors: string[];
    enrolled: number[];
  };
}

interface BaseDoceboCourseSessionsResponse {
  data: {
    sessions: DoceboClassroomSession[] | DoceboWebinarSession[];
  };
}

interface DoceboClassroomSessionsResponse
  extends BaseDoceboCourseSessionsResponse {
  data: {
    sessions: DoceboClassroomSession[];
    locations: DoceboClassroomLocation[];
  };
}

interface DoceboWebinarSessionsResponse
  extends BaseDoceboCourseSessionsResponse {
  data: {
    sessions: DoceboWebinarSession[];
  };
}

export type DoceboCourseSessionsResponse =
  | DoceboClassroomSessionsResponse
  | DoceboWebinarSessionsResponse;

export interface DoceboWebinarSession {
  id: string;
  name: string;
  description: string;
  start_date: string;
  end_date: string;
  is_enrolled: false;
  dates: DoceboWebinarSessionDate[];
  slug_name: string;
  evaluation_type: string;
}

export interface DoceboWebinarSessionDate {
  day: string;
  name: string;
  description: string;
  duration: number;
  start_hour: string;
  timezone: string;
  tool: string;
  url: string;
  user_status: string;
  webinar_tool: string;
}

export interface DoceboClassroomSession {
  id: number;
  name: string;
  description: string;
  start_date: string;
  end_date: string;
  is_enrolled: boolean;
  dates: DoceboClassroomSessionDate[];
  begin_day: string;
  begin_time: string;
  begin_timezone: string;
  end_day: string;
  end_time: string;
  end_timezone: string;
  current_location: number;
  slug_name: string;
}

export interface DoceboClassroomSessionDate {
  day: string;
  name: string;
  utc_begin: string;
  time_begin: string;
  time_end: string;
  timezone: string;
  id_location: number;
  id_classroom: number;
  user_status: 'not_attended' | 'attended';
}

export interface DoceboClassroomLocation {
  name: string;
  address: string;
  country_name: string;
  country_code: string;
  telephone: string;
  email: string;
  id_location: number;
}

export interface DoceboEnrolUserInLearningPlanResponse {
  data: {};
  enrolled: {
    id_path: number;
    id_user: number;
    waiting: boolean;
  }[];
}

export interface DoceboCourseSessionEnrolmentResponse {
  data: {
    success: boolean;
    error_message: string;
  };
}

export interface DoceboJoinWebinarSessionResponse {
  data: {
    join_url: string;
  };
}

export interface DoceboJoinSessionEventResponse {
  data: {
    join_url: string;
  };
}

export interface DoceboResetCourseResponse {
  data: {
    success: boolean;
    messages: string[];
  };
}

export interface DoceboLoTrackVideo {
  track: {
    bookmark: number;
    complete?: boolean;
  };
}

export type DoceboLoTrackType = DoceboLoTrackVideo;

export type DoceboLoTrackResponse =
  | DoceboLoTrackResponseEmpty
  | DoceboLoTrackResponseHTML;

export interface DoceboLoTrackResponseEmpty {
  data: [];
}

export interface DoceboLoTrackResponseHTML {
  data: {
    acknowledge: {
      contents: string;
    };
  };
}

export default class DoceboAPI {
  private static singleton: DoceboAPI | null = null;

  private hostname!: string;

  private oAuthResponse!: DoceboOAuth2Response;

  private userSessionInfo!: DoceboUserSessionInfo;

  public static get instance(): DoceboAPI {
    if (!DoceboAPI.singleton) {
      DoceboAPI.singleton = new DoceboAPI();
    }
    return DoceboAPI.singleton;
  }

  public setHostname(hostname: string) {
    this.hostname = hostname;
  }

  public initToken(jwt: string): Promise<void> {
    const fd = new FormData();
    fd.append('grant_type', 'urn:ietf:params:oauth:grant-type:jwt-bearer');
    fd.append('scope', 'api');
    fd.append('assertion', jwt);

    return fetch(`${this.hostname}/oauth2/token`, {
      method: 'POST',
      body: fd,
    })
      .then((response) => {
        if (response.status === 200) return response.json();
        return response.json().then((error) => Promise.reject(error));
      })
      .then((oAuthResponse) => {
        this.oAuthResponse = oAuthResponse;
      });
  }

  public initUserSessionInfo(): Promise<void> {
    return (this.request('/manage/v1/user/session') as Promise<
      DoceboUserSessionInfo
    >).then((response) => {
      this.userSessionInfo = response;
    });
  }

  public getUserInfo(): Promise<DoceboUserInfo> {
    if (!this.userSessionInfo)
      return Promise.reject(
        new Error(
          'No user session info, call initUserSessionInfo first to perform requests.'
        )
      );
    return this.request(
      `/manage/v1/user/${this.userSessionInfo.data.id}`
    ) as Promise<DoceboUserInfo>;
  }

  /**
   * Logs the user into Docebo in the background with the supplied OAuth2 token
   * @param response The OAuth2 token response object for the user
   */
  public loginUserWithAccessToken(): Promise<void> {
    return new Promise((resolve) => {
      // Construct login URL using response params
      const url = `${this.hostname}/learn;type=oauth2_response;access_token=${
        this.oAuthResponse.access_token
      };expires_in=${
        this.oAuthResponse.expires_in
      };token_type=${this.oAuthResponse.token_type.toLowerCase()};scope=${
        this.oAuthResponse.scope
      }`;

      // Create hidden iframe and load URL
      const iframe: HTMLIFrameElement = document.createElement('iframe');
      iframe.src = url;
      iframe.style.display = 'block';
      iframe.style.width = '0';
      iframe.style.height = '0';
      iframe.style.border = '0';
      iframe.style.visibility = 'hidden';

      document.body.appendChild(iframe);

      /**
       * IMPLEMENTATION NO LONGER WORKS - DOCEBO IS NO LONGER EMITTING MESSAGES
       */

      // Listen to window.postMessage events from Docebo to destroy iframe when complete
      // const windowMessageHandler = (event: MessageEvent) => {
      //   console.log(event);
      //   let data: {
      //     type: string;
      //     guid: string;
      //   };
      //   try {
      //     data = JSON.parse(event.data);
      //     console.info(data);
      //     if (data.type === 'load') {
      //       window.removeEventListener('message', windowMessageHandler);
      //       document.body.removeChild(iframe);
      //       resolve();
      //     }
      //   } catch (error) {
      //     console.warn(error);
      //   }
      // };
      // window.addEventListener('message', windowMessageHandler);

      /**
       * TEMPORARY WORKAROUND - Wait 10 seconds to ensure/hope Docebo has run through its authentication steps and set the cookie before advancing
       */
      setTimeout(() => resolve(), 10000);
    });
  }

  public getCatalogueView(catalogueId: number): Promise<DoceboCatalogueView> {
    return this.request(`/learn/v1/catalog/${catalogueId}`) as Promise<
      DoceboCatalogueView
    >;
  }

  public getCourseEnrolments(
    courseIds: number[],
    learningPlanIds: number[]
  ): Promise<(DoceboEnrolmentItemCourse | DoceboEnrolmentItemLearningPlan)[]> {
    if (!this.userSessionInfo)
      return Promise.reject(
        new Error(
          'No user session info, call initUserSessionInfo first to perform requests.'
        )
      );
    return this.getPaginatedCourseEnrolments(courseIds, learningPlanIds);
  }

  private getPaginatedCourseEnrolments(
    courseIds: number[],
    learningPlanIds: number[],
    enrolments: (
      | DoceboEnrolmentItemCourse
      | DoceboEnrolmentItemLearningPlan
    )[] = [],
    pageNumber?: number
  ): Promise<(DoceboEnrolmentItemCourse | DoceboEnrolmentItemLearningPlan)[]> {
    let queryParams = `?id_user=${this.userSessionInfo.data.id}&page_size=200`;
    courseIds.forEach((courseId) => {
      queryParams += `&id_course[]=${courseId}`;
    });
    learningPlanIds.forEach((learningPlanId) => {
      queryParams += `&id_learning_plan[]=${learningPlanId}`;
    });
    if (pageNumber) {
      queryParams += `&page=${pageNumber}`;
    }
    return (this.request(`/learn/v1/enrollments${queryParams}`) as Promise<
      DoceboEnrolments
    >).then((enrolmentResponse: DoceboEnrolments) => {
      const combinedEnrolments = enrolments.concat(
        enrolmentResponse.data.items
      );
      // eslint-disable-next-line no-underscore-dangle
      if (enrolmentResponse.data.has_more_data) {
        // eslint-disable-next-line no-underscore-dangle
        const nextPageNumber = enrolmentResponse.data.current_page + 1;
        return this.getPaginatedCourseEnrolments(
          courseIds,
          learningPlanIds,
          combinedEnrolments,
          nextPageNumber
        );
      }
      return combinedEnrolments;
    });
  }

  public getCourseDetail(courseId: string): Promise<DoceboCourseDetail> {
    return this.request(`/learn/v1/courses/${courseId}`) as Promise<
      DoceboCourseDetail
    >;
  }

  public enrolOnLearningPlan(
    learningPlanId: string
  ): Promise<DoceboEnrolUserInLearningPlanResponse> {
    return this.request(
      `/learn/v1/lp/enroll`,
      'POST',
      JSON.stringify({
        // eslint-disable-next-line @typescript-eslint/camelcase
        id_lp: learningPlanId,
        // eslint-disable-next-line @typescript-eslint/camelcase
        id_user: this.userSessionInfo.data.id,
      })
    ) as Promise<DoceboEnrolUserInLearningPlanResponse>;
  }

  public getLearningPlanDetail(
    learningPlanId: number
  ): Promise<DoceboLearningPlanDetail> {
    return this.request(`/learn/v1/lp/${learningPlanId}`) as Promise<
      DoceboLearningPlanDetail
    >;
  }

  public getLearningObjectPlayDetail(
    loId: string
  ): Promise<DoceboLearningObjectPlayDetail> {
    // Force device type to mobile so the player's toolbar is not visible
    const device: 'tablet' | 'desktop' | 'smartphone' | 'mobile' = 'mobile';
    return this.request(
      `/learn/v1/lo/${loId}/play?device=${device}`
    ) as Promise<DoceboLearningObjectPlayDetail>;
  }

  public getLearningObjectTrackDetail(
    loId: string
  ): Promise<DoceboLearningObjectTrackDetail> {
    return this.request(`/learn/v1/lo/${loId}/track`, 'POST') as Promise<
      DoceboLearningObjectTrackDetail
    >;
  }

  public getCourseSessions(
    courseId: string
  ): Promise<DoceboCourseSessionsResponse> {
    return this.request(`/learn/v1/courses/${courseId}/sessions`) as Promise<
      DoceboCourseSessionsResponse
    >;
  }

  public enrolOnCourseSession(
    courseId: string,
    sessionId: string
  ): Promise<DoceboCourseSessionEnrolmentResponse> {
    return this.request(
      `/learn/v1/enrollments/${courseId}/${this.userSessionInfo.data.id}`,
      'POST',
      JSON.stringify({
        // eslint-disable-next-line @typescript-eslint/camelcase
        session_id: sessionId,
      })
    ) as Promise<DoceboCourseSessionEnrolmentResponse>;
  }

  public enrolOnCourse(
    courseId: string
  ): Promise<DoceboEnrolUserInCourseResponse> {
    return this.request(
      `/learn/v1/enrollments/${courseId}/${this.userSessionInfo.data.id}`,
      'POST'
    ) as Promise<DoceboEnrolUserInCourseResponse>;
  }

  public resetCourse(courseId: string): Promise<DoceboResetCourseResponse> {
    return this.request(
      `/learn/v1/courses/${courseId}/trackings`,
      'DELETE',
      JSON.stringify({
        options: {
          // eslint-disable-next-line @typescript-eslint/camelcase
          include_elearning_courses_without_trackings: true,
          // eslint-disable-next-line @typescript-eslint/camelcase
          reset_lrs: true,
        },
      })
    ) as Promise<DoceboResetCourseResponse>;
  }

  public resetLearningPlan(
    learningPlanId: string
  ): Promise<DoceboResetCourseResponse[]> {
    return this.getLearningPlanDetail(Number(learningPlanId)).then(
      (learningPlanDetail) => {
        const courseResetPromiseArray: Promise<
          DoceboResetCourseResponse
        >[] = [];
        learningPlanDetail.data.courses.forEach((course) => {
          courseResetPromiseArray.push(
            this.resetCourse(course.idCourse.toString())
          );
        });
        return Promise.all(courseResetPromiseArray);
      }
    );
  }

  /**
   * @deprecated Docebo no longer offering webinar support, use ILT session events instead.
   */
  public joinWebinarSession(
    courseId: string,
    sessionId: string,
    date: string
  ): Promise<DoceboJoinWebinarSessionResponse> {
    return this.request(
      `/learn/v1/courses/${courseId}/session/${sessionId}/date/${date}/join`
    ) as Promise<DoceboJoinWebinarSessionResponse>;
  }

  public joinSessionEvent(
    eventId: string
  ): Promise<DoceboJoinSessionEventResponse> {
    return this.request(`/course/v1/events/${eventId}/join_webtool`) as Promise<
      DoceboJoinSessionEventResponse
    >;
  }

  public trackLo(
    loId: string,
    data: DoceboLoTrackType
  ): Promise<DoceboLoTrackResponse> {
    return this.request(
      `/learn/v1/lo/${loId}/track`,
      'POST',
      JSON.stringify(data)
    ) as Promise<DoceboLoTrackResponse>;
  }

  private request(
    path: string,
    method?: RequestInit['method'],
    body?: RequestInit['body']
  ): Promise<unknown> {
    if (!this.oAuthResponse)
      return Promise.reject(
        new Error('No access token, call getToken first to perform requests.')
      );
    return fetch(`${this.hostname}${path}`, {
      ...(method ? { method } : null),
      headers: {
        Authorization: `Bearer ${this.oAuthResponse.access_token}`,
      },
      ...(body ? { body } : null),
    }).then((response) => {
      if (response.status === 200) return response.json();
      return response.json().then((error) => Promise.reject(error));
    });
  }
}
